import React from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Toaster } from "react-hot-toast";

import { Routes as AppRoutes } from "./routes";
import Login from "./features/auth/login/Login";
import CreatePassword from "./features/auth/createPassword/CreatePassword";
import ResetPassword from "./features/auth/resetPassword/ResetPassword";
import ForgetPassword from "./features/auth/forgetPassword/ForgetPassword.jsx";
import { isTokenExpired } from "./helpers/AccessControlUtils";
import AdminHeader from "./components/layout/header/AdminHeader.jsx";
import AuthGateLayout from "./components/layout/AuthGateLayout.jsx";
import DashBoard from "./features/dashboard/DashBoard.jsx";
import NotFound from "./NotFound.jsx";
import Orders from "./features/orders/Orders.jsx";
import OrderDetails from "./features/orders/OrderDetails.jsx";
import Prescription from "./features/prescription/Prescription.jsx";
import OrderStatusView from "./features/orders/OrderStatusView.jsx";
import Lenconfirm from "./features/prescription/Lenconfirm.jsx";
import ScrollToTop from "./components/scrollToTop/ScrollToTop.jsx";
import ProtectedOrdersRoute from "./features/auth/ProtectedOrdersRoute.jsx";
import RequirePermission from "./features/auth/RequirePermission.jsx";
import RequireAdmin from "./features/auth/RequireAdmin.jsx";
import RoleHomeRedirect from "./features/auth/RoleHomeRedirect.jsx";
import {
  RequireAdminOrdersHome,
  RequireVaDistributorHome,
  RequireVaSettings,
} from "./features/auth/RoleHomeGuards.jsx";
import AccessDenied from "./features/auth/AccessDenied.jsx";
import VaDistributorHome from "./features/distributor/VaDistributorHome.jsx";
import VaDashboardSettings from "./features/distributor/VaDashboardSettings.jsx";
import VaDashboardTransactions from "./features/distributor/VaDashboardTransactions.jsx";
import VaDistributorHospitalVolume from "./features/distributor/VaDistributorHospitalVolume.jsx";
import AudiologistHome from "./features/audiologist/AudiologistHome.jsx";
import AudiologistLogin from "./features/audiologist/AudiologistLogin.jsx";
import RequireRole from "./features/auth/RequireRole.jsx";
import VaDistributorsPage from "./features/admin/va-distributors/VaDistributorsPage.jsx";
import VaDistributorDetailsPage from "./features/admin/va-distributors/VaDistributorDetailsPage.jsx";
import AdminSidebar, {
  readSidebarCollapsed,
  writeSidebarCollapsed,
} from "./components/layout/AdminSidebar.jsx";
import RedemptionCodesPage from "./features/redemption/RedemptionCodesPage.jsx";
import AppVersions from "./features/appVersions/AppVersions.jsx";
import AbnormalLensOrdersModal from "./features/orders/AbnormalLensOrdersModal.jsx";
import { GlobalLoadingBar } from "./components/shared/loader/LoadingBar.jsx";
import "./components/layout/adminSidebar.css";
import { AuthProvider, useAuth } from "./features/auth/AuthContext";
import { P } from "./utils/permissions";
import McpAuthorizationPage from "./features/mcp/McpAuthorizationPage";

function DefaultRouteHandler() {
  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromUrl = urlParams.get("token");

  if (tokenFromUrl) {
    localStorage.setItem("authToken", tokenFromUrl);
    const newUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, "", newUrl);
  }

  const isUserLoggedIn = localStorage.getItem("authToken");
  const isAuthenticated = isUserLoggedIn && !isTokenExpired();

  return isAuthenticated ? (
    <Navigate to="/" replace />
  ) : (
    <Login />
  );
}

function PublicLayout() {
  return <Outlet />;
}

function AdminShell() {
  const location = useLocation();
  const [intialSearch, setIntialSearch] = React.useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(
    readSidebarCollapsed
  );
  const { hasAnyPermission, hasPermission, isAudiologist } = useAuth();
  const abnormalLensAlert = (
    <AbnormalLensOrdersModal enabled={hasPermission(P.ORDERS_READ_ALL)} />
  );

  const isOrderDetailsPage = /^\/orders\/order-details\//.test(location.pathname);
  const isRedemptionCodesPage = location.pathname === AppRoutes.RedemptionCodes.path;
  const isAppVersionsPage = location.pathname === AppRoutes.AppVersions.path;
  const isAudiologistUser =
    isAudiologist() &&
    !hasPermission(P.ORDERS_READ_ALL) &&
    !hasPermission(P.ORDERS_READ_ASSIGNED);

  const showSidebar =
    !isOrderDetailsPage &&
    !isAudiologistUser &&
    (hasAnyPermission(
      P.ORDERS_READ_ALL,
      P.ORDERS_READ_VA,
      P.ORDERS_READ_ASSIGNED,
      P.ORDERS_READ_OWN
    ) ||
    hasPermission(P.VA_DISTRIBUTOR_MANAGE) ||
    hasPermission(P.VA_INVOICE_UPLOAD) ||
    isRedemptionCodesPage ||
    isAppVersionsPage);

  const toggleSidebarCollapsed = React.useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      writeSidebarCollapsed(next);
      return next;
    });
  }, []);

  if (isOrderDetailsPage) {
    return (
      <>
      {abnormalLensAlert}
      <div className="wrapper p-0 admin-shell-order-details">
        <AdminHeader
          intialSearch={intialSearch}
          setIntialSearch={setIntialSearch}
        />
        <div className="order-details-page-wrapper">
          <Outlet context={{ intialSearch, setIntialSearch }} />
        </div>
      </div>
      </>
    );
  }

  if (!showSidebar) {
    return (
      <>
      {abnormalLensAlert}
      <div className="wrapper p-0">
        <AdminHeader
          intialSearch={intialSearch}
          setIntialSearch={setIntialSearch}
        />
        <div className="inner_wrapper p-3">
          <Outlet context={{ intialSearch, setIntialSearch }} />
        </div>
      </div>
      </>
    );
  }

  return (
    <>
    {abnormalLensAlert}
    <div
      className={`wrapper p-0 admin-shell-sidebar${
        sidebarCollapsed ? " admin-shell-sidebar--collapsed" : ""
      }`}
    >
      <div className="admin-layout admin-layout--shell">
        <AdminSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapsed={toggleSidebarCollapsed}
        />
        <div className="admin-main-column">
          <div className="admin-layout-content admin-layout-content--shell">
            <Outlet context={{ intialSearch, setIntialSearch }} />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

function App() {
  const error = useSelector((state) => state.global.error);

  // Standalone consent can use the existing server login session; do not auto-redirect via AuthProvider.
  if (["/mcp/authorize", "/mcp/connections"].includes(window.location.pathname)) {
    return <McpAuthorizationPage />;
  }

  return (
    <AuthProvider>
    <BrowserRouter>
      <ScrollToTop />
      <GlobalLoadingBar />
      <Toaster
        position="bottom-center"
        reverseOrder={false}
        toastOptions={{
          className: (toast) =>
            toast.type === "success"
              ? "toast-success"
              : toast.type === "error"
                ? "toast-error"
                : "toast-default",
          style: { padding: "10px", bottom: "140px", top: "unset" },
        }}
      />
      {!error && (
        <Routes>
          <Route element={<PublicLayout />}>
            {!(
              localStorage.getItem("authToken") && !isTokenExpired()
            ) && (
              <Route
                path={AppRoutes.Default.path}
                element={<DefaultRouteHandler />}
              />
            )}
            <Route path={AppRoutes.Login.path} element={<Login />} />
            <Route
              path={AppRoutes.AudiologistLogin.path}
              element={<AudiologistLogin />}
            />
            <Route
              path={AppRoutes.CreatePassword.path}
              element={<CreatePassword />}
            />
            <Route
              path={AppRoutes.ResetPassword.path}
              element={<ResetPassword />}
            />
            <Route
              path={AppRoutes.ForgotPassword.path}
              element={<ForgetPassword />}
            />
            <Route
              path={AppRoutes.Prescription.path}
              element={<Prescription />}
            />
            <Route
              path={AppRoutes.OrderStatusView.path}
              element={<OrderStatusView />}
            />
            <Route path={AppRoutes.Lenconfirm.path} element={<Lenconfirm />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route element={<AuthGateLayout />}>
            <Route path={AppRoutes.Default.path} element={<RoleHomeRedirect />} />

            <Route element={<AdminShell />}>
              <Route
                path={AppRoutes.AccessDenied.path}
                element={<AccessDenied />}
              />
              <Route
                element={
                  <RequirePermission permissions={[P.ORDERS_READ_ALL]} />
                }
              >
                <Route path={AppRoutes.DashBoard.path} element={<DashBoard />} />
              </Route>
              <Route element={<ProtectedOrdersRoute />}>
                <Route element={<RequireAdminOrdersHome />}>
                  <Route path={AppRoutes.Orders.path} element={<Orders />} />
                </Route>
                <Route
                  path={AppRoutes.OrderDetails.path}
                  element={<OrderDetails />}
                />
              </Route>
              <Route
                path={AppRoutes.RedemptionCodes.path}
                element={
                  <RequireAdmin>
                    <RedemptionCodesPage />
                  </RequireAdmin>
                }
              />
              <Route element={<RequireVaSettings />}>
                <Route
                  path={AppRoutes.VaDistributorSettings.path}
                  element={<VaDashboardSettings />}
                />
              </Route>
              <Route
                path={AppRoutes.AppVersions.path}
                element={
                  <RequireAdmin>
                    <AppVersions />
                  </RequireAdmin>
                }
              />
              <Route element={<RequireVaDistributorHome />}>
                <Route
                  path={AppRoutes.VaDistributorHome.path}
                  element={<VaDistributorHome />}
                />
                <Route
                  path={AppRoutes.VaDistributorTransactions.path}
                  element={<VaDashboardTransactions />}
                />
                <Route
                  path={AppRoutes.VaDistributorHospitalVolume.path}
                  element={<VaDistributorHospitalVolume />}
                />
              </Route>
              <Route
                element={<RequireRole allowedRoles={["audiologist"]} />}
              >
                <Route
                  path={AppRoutes.AudiologistHome.path}
                  element={<AudiologistHome />}
                />
              </Route>

              <Route
                element={
                  <RequirePermission
                    permissions={[P.VA_DISTRIBUTOR_MANAGE]}
                  />
                }
              >
                <Route
                  path={AppRoutes.VaDistributorsAdmin.path}
                  element={<VaDistributorsPage />}
                />
                <Route
                  path={AppRoutes.VaDistributorDetails.path}
                  element={<VaDistributorDetailsPage />}
                />
              </Route>
              <Route
                element={
                  <RequirePermission permissions={[P.VA_INVOICE_UPLOAD]} />
                }
              >
                <Route
                  path={AppRoutes.CreateOrderUpload.path}
                  element={<Navigate to="/orders?create=upload" replace />}
                />
                <Route
                  path={AppRoutes.CreateOrderReview.path}
                  element={<Navigate to="/orders?create=review" replace />}
                />
                <Route
                  path={AppRoutes.CreateOrderManual.path}
                  element={<Navigate to="/orders?create=manual" replace />}
                />
              </Route>
            </Route>
          </Route>
        </Routes>
      )}
    </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
